import React, { useState } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell
} from 'recharts';
import { 
  Clock, Calendar, ArrowUpRight, ArrowDownRight, 
  Sun, Sunset, Moon, AlertCircle, Filter
} from 'lucide-react';

// Import mock data
import { hourlySales, weeklyHourlyHeatmap } from '../../data/analyticsData';

// Helper for formatting currency
const formatCurrency = (value) => {
  return value.toLocaleString('en-SA', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
        <div className="space-y-1.5">
          {payload.map((entry, index) => (
            <p 
              key={index} 
              className="text-sm" 
              style={{ color: entry.color }}
            >
              {entry.name}: {
                entry.name.includes('Sales') ? formatCurrency(entry.value) :
                entry.value.toLocaleString()
              }
            </p>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// Heatmap cell component
const HeatmapCell = ({ x, y, width, height, value }) => {
  // Color scale from cool to warm based on value (0-100)
  const getColor = (value) => {
    const intensity = Math.min(255, Math.round((value / 100) * 255));
    
    if (value < 30) {
      // Cool blue for low values
      return `rgb(59, 130, 246, ${value / 100})`;
    } else if (value < 70) {
      // Medium values in green
      return `rgb(16, 185, 129, ${value / 100})`;
    } else {
      // Hot values in red
      return `rgb(239, 68, 68, ${value / 100})`;
    }
  };
  
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={getColor(value)}
      stroke="#fff"
      strokeWidth={1}
      rx={2}
      ry={2}
    />
  );
};

// Heatmap tooltip
const HeatmapTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="text-sm font-medium text-gray-900 mb-2">
          {data.day} at {data.hour}
        </p>
        <div className="space-y-1.5">
          <p className="text-sm text-blue-600">
            Sales: {formatCurrency(data.sales)}
          </p>
          <p className="text-sm text-green-600">
            Transactions: {data.transactions.toLocaleString()}
          </p>
          <p className="text-sm text-gray-600">
            Activity Level: {data.value.toFixed(0)}%
          </p>
        </div>
      </div>
    );
  }
  return null;
};

// Hour segment card component
const HourSegmentCard = ({ title, hours, icon: Icon, color }) => {
  // Calculate total sales for the hours
  const totalSales = hourlySales
    .filter(hour => hours.includes(parseInt(hour.hour)))
    .reduce((sum, hour) => sum + hour.sales, 0);
  
  // Calculate average transactions for the hours
  const avgTransactions = hourlySales
    .filter(hour => hours.includes(parseInt(hour.hour)))
    .reduce((sum, hour) => sum + hour.transactions, 0) / hours.length;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && (
          <div className={`p-2 rounded-lg bg-${color}-50 dark:bg-${color}-900/20`}>
            <Icon className={`w-5 h-5 text-${color}-500 dark:text-${color}-400`} />
          </div>
        )}
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
      </div>
      
      <div className="mt-2">
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {formatCurrency(totalSales)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {hours.map(h => `${h}:00`).join(', ')}
        </p>
      </div>
      
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Avg. Transactions/Hour</span>
          <span className="font-medium text-gray-900 dark:text-white">{Math.round(avgTransactions)}</span>
        </div>
      </div>
    </div>
  );
};

const HourlySales = ({ filters }) => {
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
  const [selectedDay, setSelectedDay] = useState('All Days');
  
  // Process data for the heatmap
  const flattenedHeatmapData = weeklyHourlyHeatmap.flatMap(dayData => 
    dayData.hours.map(hourData => ({
      day: dayData.day,
      ...hourData
    }))
  );
  
  // Filter data based on selected day
  const filteredHeatmapData = selectedDay === 'All Days' 
    ? flattenedHeatmapData 
    : flattenedHeatmapData.filter(data => data.day === selectedDay);
  
  // Find peak hour
  const peakHour = [...hourlySales].sort((a, b) => b.sales - a.sales)[0];
  
  // Calculate period breakdowns
  const morningHours = [6, 7, 8, 9, 10, 11];
  const lunchHours = [12, 13, 14];
  const afternoonHours = [15, 16, 17, 18];
  const eveningHours = [19, 20, 21, 22, 23];
  
  return (
    <div className="space-y-6">
      {/* Hourly breakdown cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <HourSegmentCard 
          title="Morning Sales"
          hours={morningHours}
          icon={Sun}
          color="amber"
        />
        <HourSegmentCard 
          title="Lunch Rush"
          hours={lunchHours}
          icon={Sun}
          color="red"
        />
        <HourSegmentCard 
          title="Afternoon Sales"
          hours={afternoonHours}
          icon={Sunset}
          color="orange"
        />
        <HourSegmentCard 
          title="Evening Sales"
          hours={eveningHours}
          icon={Moon}
          color="indigo"
        />
      </div>
      
      {/* Hourly sales chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Hourly Sales Distribution
          </h3>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg p-1">
              <button
                onClick={() => setViewMode('daily')}
                className={`px-3 py-1.5 rounded text-sm font-medium ${
                  viewMode === 'daily' 
                    ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400' 
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Daily View
              </button>
              <button
                onClick={() => setViewMode('weekly')}
                className={`px-3 py-1.5 rounded text-sm font-medium ${
                  viewMode === 'weekly' 
                    ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600 dark:text-blue-400' 
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Weekly Heatmap
              </button>
            </div>
          </div>
        </div>
        
        <div className="h-[400px]">
          {viewMode === 'daily' ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={hourlySales}
                margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis 
                  yAxisId="left"
                  tickFormatter={(value) => formatCurrency(value).replace('SAR ', '')}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="sales" 
                  name="Sales" 
                  fill="#3b82f6" 
                >
                  {hourlySales.map((entry, index) => {
                    // Color the bars based on time of day
                    let fill = '#3b82f6'; // Default blue
                    
                    const hour = parseInt(entry.hour);
                    if (morningHours.includes(hour)) {
                      fill = '#f59e0b'; // Amber for morning
                    } else if (lunchHours.includes(hour)) {
                      fill = '#ef4444'; // Red for lunch
                    } else if (afternoonHours.includes(hour)) {
                      fill = '#f97316'; // Orange for afternoon
                    } else if (eveningHours.includes(hour)) {
                      fill = '#6366f1'; // Indigo for evening
                    }
                    
                    // Highlight peak hour
                    const isPeak = entry.hour === peakHour.hour;
                    
                    return (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={fill} 
                        fillOpacity={isPeak ? 1 : 0.8}
                        stroke={isPeak ? '#000' : undefined}
                        strokeWidth={isPeak ? 1 : 0}
                      />
                    );
                  })}
                </Bar>
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="transactions" 
                  name="Transactions" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Weekly Hourly Heatmap
                </h4>
                
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <select
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-sm"
                  >
                    <option value="All Days">All Days</option>
                    {weeklyHourlyHeatmap.map(day => (
                      <option key={day.day} value={day.day}>{day.day}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="h-[350px] overflow-auto">
                <div className="min-w-[700px] h-full">
                  {/* Custom heatmap visualization */}
                  <div className="grid grid-cols-1 gap-3">
                    {/* Hour labels */}
                    <div className="flex items-center">
                      <div className="w-16"></div>
                      <div className="flex-1 flex">
                        {Array.from({ length: 24 }, (_, i) => (
                          <div key={i} className="flex-1 text-center text-xs text-gray-500 dark:text-gray-400">
                            {i}:00
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Generate rows for each day or a single day */}
                    {selectedDay === 'All Days' ? (
                      weeklyHourlyHeatmap.map(dayData => (
                        <div key={dayData.day} className="flex items-center">
                          <div className="w-16 text-xs text-gray-600 dark:text-gray-400 pr-2 text-right">
                            {dayData.day}
                          </div>
                          <div className="flex-1 flex gap-1">
                            {dayData.hours.map((hourData, i) => (
                              <div 
                                key={i}
                                className="flex-1 rounded-md overflow-hidden relative group cursor-pointer"
                                style={{
                                  backgroundColor: `rgba(${
                                    hourData.value < 30 ? '59, 130, 246' : 
                                    hourData.value < 70 ? '16, 185, 129' : 
                                    '239, 68, 68'
                                  }, ${hourData.value / 100})`,
                                  height: '24px'
                                }}
                                title={`${dayData.day} at ${hourData.hour}: ${formatCurrency(hourData.sales)}`}
                              >
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/20 transition-opacity">
                                  <span className="text-xs font-medium text-white">
                                    {hourData.value.toFixed(0)}%
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center">
                        <div className="w-16 text-xs text-gray-600 dark:text-gray-400 pr-2 text-right">
                          {selectedDay}
                        </div>
                        <div className="flex-1 flex gap-1">
                          {weeklyHourlyHeatmap
                            .find(day => day.day === selectedDay)
                            ?.hours.map((hourData, i) => (
                              <div 
                                key={i}
                                className="flex-1 rounded-md overflow-hidden relative group cursor-pointer"
                                style={{
                                  backgroundColor: `rgba(${
                                    hourData.value < 30 ? '59, 130, 246' : 
                                    hourData.value < 70 ? '16, 185, 129' : 
                                    '239, 68, 68'
                                  }, ${hourData.value / 100})`,
                                  height: '24px'
                                }}
                                title={`${selectedDay} at ${hourData.hour}: ${formatCurrency(hourData.sales)}`}
                              >
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/20 transition-opacity">
                                  <span className="text-xs font-medium text-white">
                                    {hourData.value.toFixed(0)}%
                                  </span>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-6 flex items-center justify-center gap-4">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(59, 130, 246, 0.5)' }}></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Low Traffic</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.5)' }}></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">Medium Traffic</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(239, 68, 68, 0.5)' }}></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">High Traffic</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Peak Performance Analysis */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Peak Hour Analysis
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Peak Hour Metrics */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800/20">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-800/30">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h4 className="text-base font-medium text-blue-900 dark:text-blue-300">
                Peak Hour: {peakHour.hour}
              </h4>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">
                  {formatCurrency(peakHour.sales)}
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {peakHour.transactions} transactions
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Avg. Ticket</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatCurrency(peakHour.sales / peakHour.transactions)}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Customers</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {peakHour.customers}
                  </p>
                </div>
              </div>
              
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p>
                  The peak hour represents {((peakHour.sales / hourlySales.reduce((sum, h) => sum + h.sales, 0)) * 100).toFixed(1)}% of daily sales.
                </p>
              </div>
            </div>
          </div>
          
          {/* Peak Days Analysis */}
          <div className="md:col-span-2">
            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">
              Peak Hour by Day of Week
            </h4>
            
            <div className="space-y-3">
              {weeklyHourlyHeatmap.map(dayData => {
                // Find peak hour for this day
                const peakHourForDay = [...dayData.hours].sort((a, b) => b.value - a.value)[0];
                const percentOfDayTotal = ((peakHourForDay.sales / dayData.hours.reduce((sum, h) => sum + h.sales, 0)) * 100).toFixed(1);
                
                return (
                  <div key={dayData.day} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <div className="w-20 text-sm font-medium text-gray-900 dark:text-white">
                      {dayData.day}
                    </div>
                    <div className="flex-1">
                      <div className="h-4 w-full bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 dark:bg-blue-400 rounded-full"
                          style={{ width: `${peakHourForDay.value}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-32 text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {peakHourForDay.hour} ({peakHourForDay.value.toFixed(0)}%)
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {percentOfDayTotal}% of day
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Insights Box */}
      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-800/20">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800/30">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-medium text-blue-800 dark:text-blue-300">Hourly Insights</h3>
            <ul className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-300">
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Lunch rush (12:00-14:00) represents the highest sales period across all days of the week.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Weekend evenings (Friday-Saturday 19:00-22:00) show significantly higher activity than weekdays.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Morning sales (6:00-9:00) are strongest on weekdays, suggesting commuter traffic.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Consider optimizing staffing during peak hours to improve service and capitalize on high traffic periods.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HourlySales; 